import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Shield, Building2, Brain, Mic, Heart, Zap, Search, Server, Database, ChevronRight, ExternalLink } from 'lucide-react'

interface Product {
  id: string
  name: string
  series: 'jingshou' | 'kunkai'
  icon: any
  description: string
  features: string[]
  techSpecs?: string
  subModules?: Array<{ name: string; desc: string }>
  color: string
}

const productsData: Product[] = [
  {
    id: 'js700',
    name: 'JS700 基础款防欺凌预警终端',
    series: 'jingshou',
    icon: Shield,
    description: '校园防欺凌基础预警设备，实时监测异常声音，及时预警干预',
    features: [
      'AI 语音识别技术',
      '异常声音检测',
      '实时预警推送',
      '快速部署安装',
      '隐私保护设计',
      '低功耗运行',
    ],
    techSpecs: '识别准确率 98%+ | 响应时间<3s | 工作温度 -10℃~50℃',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'js800',
    name: 'JS800 标准款防欺凌智能预警终端',
    series: 'jingshou',
    icon: Shield,
    description: '智能化校园防欺凌预警系统，多维度监测，精准识别欺凌行为',
    features: [
      '多模态 AI 分析',
      '语义理解识别',
      '情绪状态分析',
      '分级预警机制',
      '数据可视化看板',
      '远程管理配置',
    ],
    techSpecs: '识别准确率 99%+ | 支持 6 种欺凌场景 | 云端本地双备份',
    color: 'from-blue-600 to-blue-400',
  },
  {
    id: 'js810',
    name: 'JS810 增强款防欺凌预警终端',
    series: 'jingshou',
    icon: Zap,
    description: '增强版防欺凌预警设备，更高精度、更多功能、更强性能',
    features: [
      '深度学习算法',
      '多方言识别支持',
      '噪声环境适应',
      '边缘计算能力',
      '离线运行模式',
      '自动固件升级',
    ],
    techSpecs: '识别准确率 99.5%+ | 支持 10+ 种方言 | 边缘 AI 芯片',
    color: 'from-indigo-500 to-purple-500',
  },
  {
    id: 'jsab110',
    name: 'JSAB110 安保终端',
    series: 'jingshou',
    icon: Brain,
    description: '专为安保人员设计，全触摸交互的报警信息处理终端',
    features: [
      '旗舰芯片处理器',
      '多种类型预警终端接入',
      '高清双向语音通话',
      '高清大屏显示清晰',
      '触摸操作丝滑流畅',
      '0成本快速学习',
    ],
    techSpecs: '接入预警终端 500+ | AI音频降噪 | 7 * 24 * 365 运行',
    color: 'from-purple-500 to-pink-500',
  },
  {
    id: 'jsjk119',
    name: 'JSJK119 健康守护终端（控烟监测）',
    series: 'jingshou',
    icon: Heart,
    description: '校园健康守护设备，智能监测吸烟等违规行为，守护学生健康',
    features: [
      '烟雾气味检测',
      '行为模式识别',
      '区域布控管理',
      '告警联动通知',
      '数据统计分析',
      '卫生间的适用',
    ],
    techSpecs: '检测灵敏度 ppm 级 | 覆盖面积 50㎡ | 误报率<1%',
    color: 'from-green-500 to-teal-500',
  },
  {
    id: 'jsx86',
    name: 'JSX86 防欺凌智能预警终端',
    series: 'jingshou',
    icon: Mic,
    description: '旗舰款防欺凌预警终端，集大成之作，全方位守护校园安全',
    features: [
      '全栈 AI 能力',
      '音视频融合分析',
      '行为轨迹追踪',
      '群体事件识别',
      '预测性预警',
      '家校联动机制',
    ],
    techSpecs: '识别准确率 99.8%+ | 支持 20+ 风险场景 | 7×24h 不间断监测',
    color: 'from-cyan-500 to-blue-500',
  },
  {
    id: 'kksp-datasummary',
    name: 'KKSP DataSummary 如易填报',
    series: 'kunkai',
    icon: Database,
    description: '企业级数据采集管理平台，简化数据填报流程，提升数据质量',
    features: [
      '可视化表单设计',
      '智能数据校验',
      '多级审批流程',
      '数据导入导出',
      '权限精细管控',
      '移动端适配',
    ],
    techSpecs: '支持 100+ 表单类型 | 日处理 100 万 + 条数据 | 99.9% 可用性',
    color: 'from-orange-500 to-red-500',
  },
  {
    id: 'kkss-assetmanagement',
    name: 'KKSS AssetManagement 资产管理',
    series: 'kunkai',
    icon: Building2,
    description: '资产数字化管理解决方案，实现资产全生命周期智能化管理',
    features: [
      '资产编码管理',
      'RFID/二维码标识',
      '使用状态追踪',
      '维护提醒通知',
      '折旧自动计算',
      '报表自动生成',
    ],
    techSpecs: '支持 10 万 + 资产 | RFID 读取距离 10m+ | 盘点效率提升 10 倍',
    color: 'from-yellow-500 to-orange-500',
  },
  {
    id: 'wisestarup-ai-platform',
    name: 'WiseStarup AI 中台套件',
    series: 'kunkai',
    icon: Server,
    description: '一站式人工智能中台系统，赋能企业快速构建 AI 应用能力',
    features: [
      '模型训练管理',
      '知识库管理',
      '对话流程管理',
      'Agent 智能体编排',
      '组织架构管理',
      'API 网关服务',
    ],
    subModules: [
      { name: '模型管理', desc: '模型训练、评估、部署全流程管理' },
      { name: '知识库管理', desc: '结构化知识存储与智能检索' },
      { name: '对话管理', desc: '可视化对话流程设计与优化' },
      { name: 'Agent 智能体', desc: '智能体编排与任务自动化' },
      { name: '组织架构', desc: '多层级组织权限管理体系' },
    ],
    techSpecs: '支持 100+ 模型 | 千万级知识库 | 千级并发对话',
    color: 'from-tech-500 to-purple-500',
  },
]

const ProductsPage = () => {
  const [activeFilter, setActiveFilter] = useState<'all' | 'jingshou' | 'kunkai'>('all')

  const filteredProducts = activeFilter === 'all' 
    ? productsData 
    : productsData.filter(p => p.series === activeFilter)

  const jingshouCount = productsData.filter(p => p.series === 'jingshou').length
  const kunkaiCount = productsData.filter(p => p.series === 'kunkai').length

  return (
    <div className="min-h-screen pt-20">
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="section-title">产品中心</h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto mb-8">
              创新驱动发展，科技引领未来
            </p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter('all')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === 'all'
                    ? 'bg-gradient-to-r from-tech-500 to-tech-400 text-white shadow-lg shadow-tech-500/30'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                全部产品 ({productsData.length})
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter('jingshou')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === 'jingshou'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/30'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                静守™系列 ({jingshouCount})
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveFilter('kunkai')}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeFilter === 'kunkai'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-500/30'
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                KK 系列 ({kunkaiCount})
              </motion.button>
            </motion.div>
          </motion.div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </motion.div>
          </AnimatePresence>

          {filteredProducts.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Search className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <p className="text-white/50">暂无产品</p>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-lg px-6 py-3 rounded-full">
              <Zap className="w-5 h-5 text-tech-400" />
              <span className="text-white/80">
                产品线持续扩展中，敬请期待更多创新产品...
              </span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

const ProductCard = ({ product, index }: { product: Product; index: number }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      layout
      className="tech-card group cursor-pointer overflow-hidden"
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${product.color} flex items-center justify-center`}>
            <product.icon className="w-7 h-7 text-white" />
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-white/40"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.div>
        </div>

        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-tech-400 transition-colors">
          {product.name}
        </h3>

        <p className="text-white/60 text-sm mb-4 line-clamp-2">
          {product.description}
        </p>

        <div className="flex items-center text-xs text-white/40 mb-4">
          <span className={`px-2 py-1 rounded bg-gradient-to-r ${product.color} text-white`}>
            {product.series === 'jingshou' ? '静守™系列' : 'KK 系列'}
          </span>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="border-t border-white/10 pt-4 mt-4">
                <h4 className="text-white font-semibold mb-3">核心功能</h4>
                <ul className="space-y-2 mb-4">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm text-white/70">
                      <div className="w-1.5 h-1.5 rounded-full bg-tech-400 mr-2 mt-1.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {product.techSpecs && (
                  <div className="bg-white/5 rounded-lg p-3 mb-4">
                    <p className="text-xs text-white/60">
                      <span className="font-semibold text-white/80">技术规格：</span>
                      {product.techSpecs}
                    </p>
                  </div>
                )}

                {product.subModules && (
                  <div className="mb-4">
                    <h4 className="text-white font-semibold mb-3">功能模块</h4>
                    <div className="space-y-2">
                      {product.subModules.map((module, idx) => (
                        <div key={idx} className="bg-gradient-to-r from-white/5 to-white/0 rounded-lg p-3">
                          <p className="text-sm text-white/80 font-medium">{module.name}</p>
                          <p className="text-xs text-white/50">{module.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full tech-button flex items-center justify-center space-x-2 text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>了解详情</span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  )
}

export default ProductsPage
